import { Component, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { DialogService } from '@shared/services/dialog';
import { DialogConfig } from '../dialog/dialog-config';
import { DialogIcon } from '../dialog/dialog-icon.enum';
import { DialogComponent } from '../dialog/dialog.component';
import { I18nService } from '@shared/i18n/i18n.service';
import { Timeout } from '@nomidio/common';
import { FormUtil, NomidioFormsService } from '@shared/forms';
import { NonNullableFormBuilder } from '@angular/forms';
import { TimeUtil } from '@shared/utils/time.util';
import { NomidioErrorsService } from '@shared/services/nomidio-component-errors/nomidio-errors.service';

@Component({
  selector: 'app-phone-pin-page',
  templateUrl: './phone-pin-page.component.html',
  styleUrls: ['./phone-pin-page.component.scss'],
  standalone: false,
})
export class PhonePinPageComponent implements OnDestroy {
  private readonly dialog = inject(DialogService);
  private readonly i18n = inject(I18nService);
  protected readonly errors = inject(NomidioErrorsService);
  private readonly nomForms = inject(NomidioFormsService);
  private readonly fb = inject(NonNullableFormBuilder);

  readonly SENDING_SMS_MSG_DELAY_MS = TimeUtil.toMilliseconds({ seconds: 10 });
  readonly RESEND_LIMIT_MSG_DELAY_MS = TimeUtil.toMilliseconds({ seconds: 10 });

  public readonly form = this.fb.group({
    pin: this.nomForms.getOtpControl(),
  });
  public get pinInput() {
    return this.form.controls.pin;
  }

  public canResendSms = true;
  public isSendingSms = false;
  public sendSmsError = false;
  public sendSmsTimeout: Timeout;
  public smsResendLimitError = false;
  public smsResendLimitErrorTimeout: Timeout;

  public couldNotVerifyError = false;
  public timeoutError = false;
  public conflictError = false;
  public showResentMessage = false;
  public busy = false;

  /**
   * The user's phone number, strictly for the purposes of displaying it to them.
   * No server interactions are performed with this phone number.
   * It is acceptable to censor this phone number.
   */
  @Input() phoneNumber: string | undefined;

  /**
   * Emits a signal to resend the MFA SMS.
   * If the SMS request fails, {@link #requestSmsFailed()} must be called.
   */
  @Output() resendSms = new EventEmitter<void>();

  /**
   * Emits a pin code the user is submitting. This should be verified by the server.
   * If the pin code is invalid, the handler must call {@link #tryAgainWithNewPin()} or {@link #couldNotVerifyFailure()}.
   */
  @Output() verifyPin = new EventEmitter<string>();

  /**
   * Trigger that the containing component should re-send an MFA code SMS to the user.
   */
  sendSms(): void {
    this.isSendingSms = true;
    this.sendSmsError = false;

    this.resendSms.emit();

    this.sendSmsTimeout = setTimeout(() => {
      this.isSendingSms = false;
    }, this.SENDING_SMS_MSG_DELAY_MS);
  }

  verify(): void {
    this.busy = true;
    this.timeoutError = false;
    this.couldNotVerifyError = false;
    this.conflictError = false;
    this.showResentMessage = false;
    this.verifyPin.emit(this.pinInput.value);
  }

  public requestSmsFailed(): void {
    this.sendSmsError = true;
    this.isSendingSms = false;
    clearTimeout(this.sendSmsTimeout);
  }

  public smsResendLimit(): void {
    this.canResendSms = false;
    this.isSendingSms = false;
    this.smsResendLimitError = true;
    clearTimeout(this.sendSmsTimeout);

    this.smsResendLimitErrorTimeout = setTimeout(() => {
      this.smsResendLimitError = false;
    }, this.RESEND_LIMIT_MSG_DELAY_MS);
  }

  public incorrectPin(): void {
    this.pinInput.setValue('');
    FormUtil.forceError(this.pinInput, { retry: true });
    this.busy = false;
  }

  /**
   * Display to the user that a new SMS has been sent to the user.
   */
  public onVerificationResent(): void {
    this.showResentMessage = true;
    this.busy = false;
  }

  public tryAgainWithNewPin(): void {
    this.busy = false;
    this.pinInput.setValue('');
    this.sendSms();

    const dialogData: DialogConfig = {
      icon: DialogIcon.AlertRed,
      content: this.i18n.getPhrase('phone_mfa.new_pin_dialog.content'),
      title: this.i18n.getPhrase('phone_mfa.new_pin_dialog.title'),
      primary: this.i18n.getPhrase('phone_mfa.new_pin_dialog.primary_button'),
    };

    this.dialog.open(DialogComponent, {
      autoFocus: true,
      data: dialogData,
      disableClose: false,
    });
  }

  public couldNotVerifyFailure(): void {
    this.busy = false;
    this.couldNotVerifyError = true;
  }

  public timeoutFailure(): void {
    this.busy = false;
    this.timeoutError = true;
    this.tryAgainWithNewPin();
  }

  public conflictFailure(): void {
    this.busy = false;
    this.conflictError = true;
  }

  ngOnDestroy() {
    clearTimeout(this.sendSmsTimeout);
  }
}
